/**
 * Which finger owns which on-screen control (docs/design/mobile-layout.md §7.2, §7.5;
 * `q-touch-controls-overlay`). Pure, so the multi-touch rules are unit-tested.
 *
 * The canvas half of the routing is structural: T1's tap/hold/orbit/pinch listeners sit on
 * the canvas element, and the overlay's controls are DOM siblings above it, so a pointer
 * that starts on a control is targeted at the control and never dispatched to the canvas.
 * This module is the overlay's half: each control (the stick, each ability slot, Dodge) has
 * at most ONE owning pointer, and a pointer owns at most one control.
 *
 * - a second finger on a held control is refused — a held slot never fires twice, and a
 *   second thumb in the stick zone never hijacks the stick;
 * - different fingers on different controls are independent (stick + a slot = cast on the
 *   move), and fingers on the canvas are never seen here at all.
 */

/** A control on the overlay: the stick, ability slot 0–5, or Dodge. */
export type TouchControl = 'stick' | 'dodge' | `slot:${number}`;

export class TouchRouter {
  private readonly byPointer = new Map<number, TouchControl>();
  private readonly byControl = new Map<TouchControl, number>();

  /**
   * Pointer `id` pressed `control`. Returns true when it now owns it — the press should act;
   * false when the control is already held by another pointer, or `id` already owns one.
   */
  claim(id: number, control: TouchControl): boolean {
    if (this.byPointer.has(id) || this.byControl.has(control)) return false;
    this.byPointer.set(id, control);
    this.byControl.set(control, id);
    return true;
  }

  /** Pointer `id` lifted or was cancelled. Returns the control it released, if any. */
  release(id: number): TouchControl | null {
    const control = this.byPointer.get(id);
    if (control === undefined) return null;
    this.byPointer.delete(id);
    this.byControl.delete(control);
    return control;
  }

  /** The control pointer `id` owns, or null. */
  controlOf(id: number): TouchControl | null {
    return this.byPointer.get(id) ?? null;
  }

  /** The pointer holding `control`, or null. */
  holderOf(control: TouchControl): number | null {
    return this.byControl.get(control) ?? null;
  }

  /** Drop every claim (blur, unmount). */
  reset(): void {
    this.byPointer.clear();
    this.byControl.clear();
  }
}

/** combat.md §Control model: six ability slots + dodge. The basic attack is a tap, not a button. */
export const TOUCH_BAR_MAX_ABILITY_SLOTS = 6;
export const TOUCH_BAR_MAX_BUTTONS = TOUCH_BAR_MAX_ABILITY_SLOTS + 1;

export interface TouchBarButton {
  control: TouchControl;
  /** The ability id for a slot; null for Dodge. */
  abilityId: string | null;
  /** 1-based slot number for a slot (the digit key it mirrors); null for Dodge. */
  slot: number | null;
}

/**
 * The touch bar's buttons for a loadout's abilities, in key order (slot 1 = key `1`): at most
 * six slots, then Dodge — never more than the seven-button ceiling, whatever the loadout says.
 */
export function touchBarButtons(abilities: readonly { id: string }[]): TouchBarButton[] {
  const slots = abilities.slice(0, TOUCH_BAR_MAX_ABILITY_SLOTS).map((a, i) => ({
    control: `slot:${i}` as TouchControl,
    abilityId: a.id,
    slot: i + 1,
  }));
  return [...slots, { control: 'dodge', abilityId: null, slot: null }];
}
